import { Box, Button, Container, Stack, Typography, useTheme, Paper } from '@mui/material';
import { SignedIn, SignedOut, SignInButton } from '@clerk/clerk-react';
import { PlayArrow, Info } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const Hero = () => {
  const theme = useTheme();

  return (
    <Box
      className="slide-down"
      sx={{
        py: { xs: 8, md: 12 },
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, rgba(26,26,26,0.8) 0%, rgba(15,15,15,1) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,1) 100%)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 50%, ${theme.palette.primary.main}15 0%, transparent 50%),
                       radial-gradient(circle at 80% 80%, ${theme.palette.primary.main}10 0%, transparent 50%)`,
          pointerEvents: 'none',
        },
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h1"
          component="h1"
          sx={{
            fontWeight: 800,
            mb: theme.spacing(3),
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #3EAEBE 0%, #A6E4EE 100%)'
                : 'linear-gradient(135deg, #3EAEBE 0%, #266A74 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            fontSize: { xs: '2.5rem', md: '3.5rem', lg: '4rem' },
          }}
        >
          Organize Your YouTube
        </Typography>

        <Typography
          variant="h5"
          component="p"
          sx={{
            mb: theme.spacing(5),
            color: 'text.secondary',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6,
            fontSize: { xs: '1.1rem', md: '1.25rem' },
          }}
        >
          Effortlessly group and manage your YouTube subscriptions. Create custom channel groups, organize your content,
          and never miss updates from your favorite creators.
        </Typography>

        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          justifyContent="center"
          sx={{ mb: theme.spacing(8) }}
        >
          <SignedOut>
            <SignInButton mode="modal">
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<PlayArrow />}
                sx={{
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: theme.shadows[4],
                  '&:hover': {
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                Get Started for Free
              </Button>
            </SignInButton>
          </SignedOut>

          <SignedIn>
            <Link to="/app">
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<PlayArrow />}
                sx={{
                  px: 5,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  boxShadow: theme.shadows[4],
                  '&:hover': {
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                Group My Channels
              </Button>
            </Link>
          </SignedIn>

          <Button
            variant="outlined"
            color="primary"
            size="large"
            startIcon={<Info />}
            href="#how-it-works"
            sx={{
              px: 5,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderWidth: 2,
              '&:hover': {
                borderWidth: 2,
              },
            }}
          >
            Learn More
          </Button>
        </Stack>

        <Paper
          elevation={theme.palette.mode === 'dark' ? 8 : 4}
          sx={{
            p: 2,
            borderRadius: (theme.shape.borderRadius as number) / 8,
            background:
              theme.palette.mode === 'dark'
                ? 'linear-gradient(145deg, #1A1A1A 0%, #282828 100%)'
                : 'linear-gradient(145deg, #FFFFFF 0%, #F5F5F5 100%)',
            maxWidth: '900px',
            mx: 'auto',
          }}
        >
          <Box
            component="img"
            src="/dashboard.png"
            alt="Dashboard Preview"
            sx={{
              width: '100%',
              height: 'auto',
              borderRadius: (theme.shape.borderRadius as number) / 8 - 1,
              border: `1px solid ${theme.palette.divider}`,
            }}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default Hero;
